import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CopyButton, Icon } from '../../components/ui';
import { DocSection, LangCode } from '../../components/DocBlocks';
import { API_KEY_ENV, BASE_URL, LANGS, chatSnippet } from '../../snippets';
import { useDocs } from './context';
import { GROUPS, PAGES, docPath } from './registry';

function Step({ n, title, children, action }: { n: number; title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <li className="docs-step">
      <span className="docs-step-num" aria-hidden>
        {n}
      </span>
      <div className="docs-step-body">
        <h3>
          <span className="sr-only">Langkah {n}: </span>
          {title}
        </h3>
        {children}
        {action && <div className="docs-step-action">{action}</div>}
      </div>
    </li>
  );
}

const SPEC: Array<{ label: string; value: ReactNode; copy?: string }> = [
  { label: 'Base URL', value: <code>{BASE_URL}</code>, copy: BASE_URL },
  { label: 'Autentikasi', value: <code>Authorization: Bearer &lt;API key&gt;</code> },
  { label: 'Format', value: 'Sama dengan OpenAI Chat Completions' },
  { label: 'Tagihan', value: 'Rupiah, dipotong dari saldo per request' },
];

export function Overview() {
  const { publicView, lang, setLang, model } = useDocs();

  return (
    <>
      <header className="docs-hero">
        <h1>Dokumentasi API</h1>
        <p className="docs-summary">
          Satu API key dan satu saldo Rupiah untuk model teks, gambar, dan video. Formatnya sama dengan OpenAI, jadi kode yang sudah
          ada cukup diarahkan ke alamat baru.
        </p>
        <dl className="docs-spec">
          {SPEC.map((row) => (
            <div key={row.label} className="docs-spec-row">
              <dt>{row.label}</dt>
              <dd>
                <span className="docs-spec-value">{row.value}</span>
                {row.copy && <CopyButton value={row.copy} className="btn-ghost" />}
              </dd>
            </div>
          ))}
        </dl>
      </header>

      <DocSection id="langkah" title="Mulai dalam 3 langkah">
        <ol className="docs-steps">
          <Step
            n={1}
            title="Daftar dan isi saldo"
            action={
              publicView ? (
                <Link className="btn btn-sm btn-primary" to="/login?daftar=1">
                  Daftar akun
                </Link>
              ) : (
                <Link className="btn btn-sm" to="/wallet">
                  <Icon name="wallet" size={15} />
                  Isi saldo
                </Link>
              )
            }
          >
            <p>Satu saldo Rupiah dipakai untuk semua model. Tidak ada biaya langganan.</p>
          </Step>
          <Step
            n={2}
            title="Buat API key"
            action={
              publicView ? (
                <Link className="btn btn-sm" to="/login">
                  Masuk ke portal
                </Link>
              ) : (
                <Link className="btn btn-sm" to="/api-keys">
                  <Icon name="key" size={15} />
                  Buat API key
                </Link>
              )
            }
          >
            <p>
              Key hanya tampil sekali. Simpan di environment variable <code>{API_KEY_ENV}</code>.
            </p>
          </Step>
          <Step
            n={3}
            title="Kirim request"
            action={
              publicView ? undefined : (
                <Link className="btn btn-sm" to="/playground">
                  <Icon name="terminal" size={15} />
                  Coba di Playground
                </Link>
              )
            }
          >
            <p>
              Pakai SDK OpenAI yang sudah ada. Cukup ganti <code>base_url</code> dan <code>api_key</code>.
            </p>
            <LangCode code={chatSnippet(lang, { model })} lang={lang} langs={LANGS} onLang={setLang} />
          </Step>
        </ol>
      </DocSection>

      <DocSection id="topik" title="Jelajahi topik">
        <div className="docs-groups">
          {GROUPS.map((group) => {
            const pages = PAGES.filter((page) => page.slug && page.group === group);
            if (pages.length === 0) return null;
            return (
              <div key={group} className="docs-group">
                <h3>{group}</h3>
                <ul>
                  {pages.map((page) => (
                    <li key={page.slug}>
                      <Link to={docPath(page.slug)} className="docs-topic">
                        <span className="docs-topic-icon">
                          <Icon name={page.icon} size={17} />
                        </span>
                        <span className="docs-topic-text">
                          <span className="docs-topic-title">{page.title}</span>
                          <span className="docs-topic-sub">{page.blurb}</span>
                        </span>
                        <Icon name="arrow" size={15} className="docs-topic-go" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </DocSection>
    </>
  );
}
